import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../config';

const PAGE_SIZE = 50;

const ClientsPage = ({ onShowToast }) => {
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);

    const load = useCallback(
        async (search = '', pageNum = 1) => {
            try {
                setLoading(true);
                const params = new URLSearchParams({ limit: String(PAGE_SIZE), page: String(pageNum) });
                if (search.trim()) params.set('q', search.trim());
                const res = await fetch(`${API_BASE_URL}/api/users/clients?${params.toString()}`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                });
                if (!res.ok) throw new Error('Failed to load clients');
                const data = await res.json();
                setItems(Array.isArray(data.items) ? data.items : []);
                setTotal(Number(data.total || 0));
                if (data.page) setPage(Number(data.page));
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    useEffect(() => {
        load('', 1);
    }, [load]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const toggleUserBlocked = async (user) => {
        const nextBlocked = !user.is_blocked;
        const res = await fetch(`${API_BASE_URL}/api/users/${user.id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ is_blocked: nextBlocked }),
        });
        if (!res.ok) {
            onShowToast?.('Не удалось изменить статус бана');
            return;
        }
        setItems((current) =>
            current.map((item) =>
                item.id === user.id ? { ...item, is_blocked: nextBlocked } : item,
            ),
        );
        onShowToast?.(nextBlocked ? 'Пользователь забанен' : 'Пользователь разбанен');
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-2xl font-bold text-slate-800">Клиенты</h2>
                <div className="text-sm text-slate-500">
                    Всего: {total}
                    {total > 0 && (
                        <span className="ml-2">
                            Стр. {page} из {totalPages}
                        </span>
                    )}
                </div>
            </div>
            <div className="bg-white border border-slate-100 rounded-xl p-4">
                <div className="flex gap-2 mb-4 flex-wrap">
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Поиск: username / ФИО / email / tg_id"
                        className="w-full min-w-[200px] max-w-xl p-2 border rounded-lg text-sm"
                    />
                    <button
                        type="button"
                        onClick={() => {
                            setPage(1);
                            load(q, 1);
                        }}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
                    >
                        Найти
                    </button>
                </div>
                <div className="flex gap-2 mb-4 items-center flex-wrap">
                    <button
                        type="button"
                        disabled={page <= 1 || loading}
                        onClick={() => {
                            const p = page - 1;
                            setPage(p);
                            load(q, p);
                        }}
                        className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-40"
                    >
                        Назад
                    </button>
                    <button
                        type="button"
                        disabled={page >= totalPages || loading}
                        onClick={() => {
                            const p = page + 1;
                            setPage(p);
                            load(q, p);
                        }}
                        className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-40"
                    >
                        Вперёд
                    </button>
                </div>
                {loading ? (
                    <div className="py-6 text-slate-500 text-center">Загрузка...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-slate-500 border-b">
                                    <th className="py-2 pr-3">ID</th>
                                    <th className="py-2 pr-3">Username</th>
                                    <th className="py-2 pr-3">Email</th>
                                    <th className="py-2 pr-3">ФИО</th>
                                    <th className="py-2 pr-3">TG ID</th>
                                    <th className="py-2 pr-3">Регистрация</th>
                                    <th className="py-2 pr-3">Последний заход</th>
                                    <th className="py-2 pr-3">Антифрод</th>
                                    <th className="py-2 pr-3">Баланс</th>
                                    <th className="py-2 pr-3">Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((u) => (
                                    <tr key={u.id} className="border-b last:border-b-0">
                                        <td className="py-2 pr-3">{u.id}</td>
                                        <td className="py-2 pr-3">
                                            <Link className="text-indigo-600 hover:underline" to={`/users/${u.id}`}>
                                                {u.username ? `@${u.username}` : '—'}
                                            </Link>
                                        </td>
                                        <td className="py-2 pr-3">{u.email || '—'}</td>
                                        <td className="py-2 pr-3">{u.fio || '—'}</td>
                                        <td className="py-2 pr-3">{u.tg_id || '—'}</td>
                                        <td className="py-2 pr-3">{u.created_at ? new Date(u.created_at).toLocaleString('ru-RU') : '—'}</td>
                                        <td className="py-2 pr-3">{u.last_seen_at ? new Date(u.last_seen_at).toLocaleString('ru-RU') : '—'}</td>
                                        <td className="py-2 pr-3">
                                            <div className="flex flex-col gap-1">
                                                <span className={u.is_blocked ? 'text-rose-600 font-semibold' : 'text-emerald-600'}>
                                                    {u.is_blocked ? 'Забанен' : 'Активен'}
                                                </span>
                                                <span className={u.captcha_passed ? 'text-slate-500' : 'text-amber-600'}>
                                                    {u.captcha_passed ? 'Капча пройдена' : 'Капча не пройдена'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-2 pr-3">{Number(u.referral_balance || 0).toFixed(2)} ₽</td>
                                        <td className="py-2 pr-3">
                                            <button
                                                type="button"
                                                onClick={() => toggleUserBlocked(u)}
                                                className={`px-3 py-1.5 rounded-lg text-xs text-white ${
                                                    u.is_blocked ? 'bg-emerald-600' : 'bg-rose-600'
                                                }`}
                                            >
                                                {u.is_blocked ? 'Разбанить' : 'Забанить'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientsPage;
